import { type GridSortModel } from '@mui/x-data-grid-pro'
import {
    GetProcessStatusesQuery,
    ProcessStatus,
    type SortField,
    type SortIndexField,
} from '__generated__/graphql'

/**
 * Orders candidate process statuses according their summary group names,
 * allowing them to be properly grouped together by components such as MUI's
 * Autocomplete input.
 *
 * @param processStatuses The array of process statuses to sort.
 * @param listDefaultValueFirst Whether to ensure the default value always appears at the beginning of the sorted array.
 * @param defaultValue The process status value to consider the default.
 * @returns A copy of the provided array of process statuses, sorted by summary group.
 */
export function sortProcessStatusesBySummaryGroup(
    processStatuses: GetProcessStatusesQuery['processStatuses'],
    listDefaultValueFirst = true,
    defaultValue: GetProcessStatusesQuery['processStatuses'][number]['status'] = ProcessStatus.NoStatus
): GetProcessStatusesQuery['processStatuses'][number][] {
    const GROUP_ORDER: Record<string, number> = {
        Mapping: 1,
        Outreach: 2,
        Interest: 3,
        Submitted: 4,
        'In Process': 5,
        'Out of Process': 6,
    }

    const sorted = processStatuses.toSorted((a, b) => {
        if (
            listDefaultValueFirst &&
            (a.status === defaultValue || b.status === defaultValue)
        ) {
            return -1
        }

        const groupA = a.processStatusSummaryGroup?.label || ''
        const groupB = b.processStatusSummaryGroup?.label || ''
        const orderA = GROUP_ORDER[groupA] ?? 999
        const orderB = GROUP_ORDER[groupB] ?? 999

        return orderA - orderB
    })
    return sorted
}

export function makeSortModel(
    activeSortFields: SortField[],
    columns: { field: string; sortIndexField?: SortIndexField }[]
): GridSortModel {
    return activeSortFields
        .map((sf) => {
            const col = columns.find((c) => c.sortIndexField === sf.field)
            return col
                ? { field: col.field, sort: sf.order as 'asc' | 'desc' }
                : null
        })
        .filter((item): item is NonNullable<typeof item> => item !== null)
}
